<template>
    <DefaultField :field="field" :errors="errors" :show-help-text="showHelpText">
        <template #field>
            <div class="ck-editor-wrapper" :class="{ 'ck-editor--dark': isDark }" :style="editorStyle">
                <div ref="editorEl"></div>
            </div>
        </template>
    </DefaultField>
</template>

<script>
import { DependentFormField, HandlesValidationErrors } from '@/mixins'
import {
    ClassicEditor, Plugin, ButtonView, IconCopy, IconEraser, IconImportExport, FileRepository,
    Essentials, Paragraph, Autoformat, TextTransformation, PasteFromOffice,
    Heading, Bold, Italic, Underline, Strikethrough, Subscript, Superscript,
    Code, Alignment, FontSize, FontFamily, FontColor, FontBackgroundColor,
    Link, AutoLink, Image, ImageCaption, ImageStyle, ImageToolbar,
    ImageUpload, ImageResize, ImageInsertViaUrl, PictureEditing,
    BlockQuote, List, ListProperties, TodoList, Indent, IndentBlock,
    Table, TableToolbar, TableProperties, TableCellProperties, TableCaption,
    TableColumnResize, MediaEmbed, CodeBlock, HorizontalLine, PageBreak,
    SpecialCharacters, SpecialCharactersEssentials, SpecialCharactersArrows,
    SpecialCharactersCurrency, SpecialCharactersMathematical, SpecialCharactersText,
    FindAndReplace, RemoveFormat, SelectAll, ShowBlocks, AccessibilityHelp, Highlight,
    SourceEditing, GeneralHtmlSupport,
} from 'ckeditor5'

class NovaUploadAdapterPlugin extends Plugin {
    init() {
        const token      = document.querySelector('meta[name="csrf-token"]')?.content ?? ''
        const uploadPath  = this.editor.config.get('novaUploadPath')  || 'ck-editor'
        const storageDisk = this.editor.config.get('novaStorageDisk') || 'public'
        this.editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
            new NovaUploadAdapter(loader, '/nova-vendor/ck-editor/upload', token, uploadPath, storageDisk)
    }
}
NovaUploadAdapterPlugin.pluginName = 'NovaUploadAdapterPlugin'
NovaUploadAdapterPlugin.requires   = [FileRepository]

function getEditorCopyContent(editor) {
    const html = editor.getData().trim()
    if (!html) {
        return ''
    }

    const withoutParagraphWrappers = html
        .replace(/<\/p>\s*<p>/gi, '\n')
        .replace(/^<p>/i, '')
        .replace(/<\/p>$/i, '')
        .replace(/<br\s*\/?>/gi, '\n')

    if (/<[a-z][^>]*>/i.test(withoutParagraphWrappers)) {
        return html
    }

    const div = document.createElement('div')
    div.innerHTML = html
    return (div.textContent || '').trim()
}

function copyToClipboard(text) {
    if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(text)
    }

    return new Promise((resolve, reject) => {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()

        try {
            document.execCommand('copy') ? resolve() : reject()
        } catch (err) {
            reject(err)
        } finally {
            document.body.removeChild(textarea)
        }
    })
}

async function readFromClipboard() {
    if (navigator.clipboard?.read) {
        try {
            const items = await navigator.clipboard.read()
            let html = ''
            let text = ''

            for (const item of items) {
                if (!html && item.types.includes('text/html')) {
                    html = await (await item.getType('text/html')).text()
                }
                if (!text && item.types.includes('text/plain')) {
                    text = await (await item.getType('text/plain')).text()
                }
            }

            if (html.trim()) {
                return html.trim()
            }

            if (text.trim()) {
                return text.trim()
            }
        } catch {
            // Fall through to readText.
        }
    }

    if (navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText()
        return text?.trim() || ''
    }

    return ''
}

function notifyNova(type, message) {
    if (typeof Nova === 'undefined') {
        return
    }

    Nova[type](message)
}

class CopyAllContentPlugin extends Plugin {
    init() {
        const editor = this.editor

        editor.ui.componentFactory.add('copyAllContent', locale => {
            const view = new ButtonView(locale)

            view.set({
                label: 'کپی محتوا',
                icon: IconCopy,
                tooltip: true,
            })

            view.on('execute', () => {
                const content = getEditorCopyContent(editor)
                if (!content) {
                    return
                }

                copyToClipboard(content)
                    .then(() => notifyNova('success', 'محتوا در کلیپ‌بورد کپی شد.'))
                    .catch(() => notifyNova('error', 'کپی محتوا انجام نشد.'))
            })

            return view
        })
    }
}
CopyAllContentPlugin.pluginName = 'CopyAllContent'

class PasteAllContentPlugin extends Plugin {
    init() {
        const editor = this.editor

        editor.ui.componentFactory.add('pasteAllContent', locale => {
            const view = new ButtonView(locale)

            view.set({
                label: 'چسباندن محتوا',
                icon: IconImportExport,
                tooltip: true,
            })

            view.on('execute', () => {
                readFromClipboard()
                    .then(content => {
                        if (!content) {
                            notifyNova('error', 'کلیپ‌بورد خالی است.')
                            return
                        }

                        editor.setData(content)
                        notifyNova('success', 'محتوا از کلیپ‌بورد جایگزین شد.')
                    })
                    .catch(() => notifyNova('error', 'چسباندن محتوا انجام نشد.'))
            })

            return view
        })
    }
}
PasteAllContentPlugin.pluginName = 'PasteAllContent'

class ClearContentPlugin extends Plugin {
    init() {
        const editor = this.editor

        editor.ui.componentFactory.add('clearContent', locale => {
            const view = new ButtonView(locale)

            view.set({
                label: 'پاک کردن محتوا',
                icon: IconEraser,
                tooltip: true,
            })

            view.on('execute', () => {
                if (!editor.getData().trim()) {
                    return
                }

                editor.setData('')
                notifyNova('success', 'محتوای ویرایشگر پاک شد.')
            })

            return view
        })
    }
}
ClearContentPlugin.pluginName = 'ClearContent'

class NovaUploadAdapter {
    constructor(loader, url, token, uploadPath, storageDisk) {
        this.loader      = loader
        this.url         = url
        this.token       = token
        this.uploadPath  = uploadPath
        this.storageDisk = storageDisk
        this.controller  = null
    }

    upload() {
        this.controller = new AbortController()
        return this.loader.file.then(file => {
            const fd = new FormData()
            fd.append('upload', file)
            fd.append('uploadPath', this.uploadPath)
            fd.append('storageDisk', this.storageDisk)
            return fetch(this.url, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': this.token },
                body: fd,
                signal: this.controller.signal,
            })
                .then(r => {
                    if (!r.ok) throw new Error(`Upload failed (${r.status})`)
                    return r.json()
                })
                .then(d => {
                    if (!d.url) throw new Error(d.message || 'Upload failed')
                    return { default: d.url }
                })
        })
    }

    abort() {
        this.controller?.abort()
    }
}

const PLUGINS = [
    Essentials,
    Paragraph,
    Autoformat,
    TextTransformation,
    PasteFromOffice,
    Heading,
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Subscript,
    Superscript,
    Code,
    Alignment,
    FontSize,
    FontFamily,
    FontColor,
    FontBackgroundColor,
    Link,
    AutoLink,
    Image,
    ImageCaption,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    ImageResize,
    ImageInsertViaUrl,
    PictureEditing,
    BlockQuote,
    List,
    ListProperties,
    TodoList,
    Indent,
    IndentBlock,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    TableCaption,
    TableColumnResize,
    MediaEmbed,
    CodeBlock,
    HorizontalLine,
    PageBreak,
    SpecialCharacters,
    SpecialCharactersEssentials,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    FindAndReplace,
    RemoveFormat,
    SelectAll,
    ShowBlocks,
    AccessibilityHelp,
    Highlight,
    SourceEditing,
    GeneralHtmlSupport,
    NovaUploadAdapterPlugin,
    CopyAllContentPlugin,
    PasteAllContentPlugin,
    ClearContentPlugin,
]

const EDITOR_CONFIG = {
    licenseKey: 'GPL',
    plugins: PLUGINS,
    htmlSupport: {
        allow: [{ name: /.*/, attributes: true, classes: true, styles: true }],
    },
    toolbar: {
        items: [
            'heading', '|',
            'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|',
            'bold', 'italic', 'underline', 'strikethrough',
            'subscript', 'superscript', 'code', 'removeFormat', '|',
            'alignment', '|',
            'bulletedList', 'numberedList', 'todoList', '|',
            'outdent', 'indent', '|',
            '-',
            'link', 'blockQuote', 'insertTable', 'uploadImage',
            'mediaEmbed', 'codeBlock', '|',
            'horizontalLine', 'pageBreak', 'specialCharacters', 'highlight', '|',
            'findAndReplace', 'showBlocks', 'selectAll',
            'copyAllContent', 'pasteAllContent', 'clearContent', 'accessibilityHelp', '|',
            'undo', 'redo', '|',
            'sourceEditing',
        ],
        shouldNotGroupWhenFull: true,
    },
    heading: {
        options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' },
        ],
    },
    fontSize: {
        options: [10, 11, 12, 14, 'default', 18, 20, 24, 28, 32, 36, 48],
    },
    fontFamily: {
        options: [
            'default',
            'VazirMatn, sans-serif',
            'Arial, Helvetica, sans-serif',
            'Courier New, Courier, monospace',
            'Georgia, serif',
            'Tahoma, Geneva, sans-serif',
            'Times New Roman, Times, serif',
            'Trebuchet MS, Helvetica, sans-serif',
            'Verdana, Geneva, sans-serif',
        ],
        supportAllValues: true,
    },
    fontColor: {
        columns: 6,
        colors: [
            { color: '#000000', label: 'Black' },
            { color: '#374151', label: 'Dark Grey' },
            { color: '#6B7280', label: 'Grey' },
            { color: '#D1D5DB', label: 'Light Grey' },
            { color: '#FFFFFF', label: 'White', hasBorder: true },
            { color: '#EF4444', label: 'Red' },
            { color: '#F97316', label: 'Orange' },
            { color: '#EAB308', label: 'Yellow' },
            { color: '#22C55E', label: 'Green' },
            { color: '#3B82F6', label: 'Blue' },
            { color: '#8B5CF6', label: 'Purple' },
            { color: '#EC4899', label: 'Pink' },
        ],
    },
    fontBackgroundColor: {
        columns: 6,
        colors: [
            { color: '#FEE2E2', label: 'Light Red' },
            { color: '#FEF3C7', label: 'Light Yellow' },
            { color: '#D1FAE5', label: 'Light Green' },
            { color: '#DBEAFE', label: 'Light Blue' },
            { color: '#EDE9FE', label: 'Light Purple' },
            { color: '#FCE7F3', label: 'Light Pink' },
            { color: '#F3F4F6', label: 'Light Grey' },
            { color: '#FFF7ED', label: 'Light Orange' },
            { color: '#ECFDF5', label: 'Mint' },
            { color: '#F0F9FF', label: 'Sky' },
            { color: '#FEFCE8', label: 'Cream' },
            { color: '#FFFFFF', label: 'White', hasBorder: true },
        ],
    },
    image: {
        toolbar: [
            'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|',
            'toggleImageCaption', 'imageTextAlternative', '|',
            'resizeImage',
        ],
        resizeOptions: [
            { name: 'resizeImage:original', value: null, label: 'Original' },
            { name: 'resizeImage:25', value: '25', label: '25%' },
            { name: 'resizeImage:50', value: '50', label: '50%' },
            { name: 'resizeImage:75', value: '75', label: '75%' },
        ],
    },
    table: {
        contentToolbar: [
            'tableColumn', 'tableRow', 'mergeTableCells',
            'tableProperties', 'tableCellProperties', 'toggleTableCaption',
        ],
    },
    list: {
        properties: { styles: true, startIndex: true, reversed: true },
    },
    link: {
        decorators: {
            openInNewTab: {
                mode: 'manual',
                label: 'Open in a new tab',
                defaultValue: false,
                attributes: { target: '_blank', rel: 'noopener noreferrer' },
            },
        },
    },
    highlight: {
        options: [
            { model: 'yellowMarker', class: 'marker-yellow', title: 'Yellow', color: '#fdfd77', type: 'marker' },
            { model: 'greenMarker', class: 'marker-green', title: 'Green', color: '#63f963', type: 'marker' },
            { model: 'pinkMarker', class: 'marker-pink', title: 'Pink', color: '#fc7899', type: 'marker' },
            { model: 'blueMarker', class: 'marker-blue', title: 'Blue', color: '#72cdfd', type: 'marker' },
            { model: 'redPen', class: 'pen-red', title: 'Red pen', color: '#e91313', type: 'pen' },
            { model: 'greenPen', class: 'pen-green', title: 'Green pen', color: '#118800', type: 'pen' },
        ],
    },
    mediaEmbed: { previewsInData: true },
}

export default {
    mixins: [DependentFormField, HandlesValidationErrors],

    props: ['resourceName', 'resourceId', 'field'],

    data() {
        return {
            editorInstance: null,
            showHelpText: true,
            isDark: document.documentElement.classList.contains('dark'),
        }
    },

    computed: {
        editorStyle() {
            const height = this.field.editorHeight || 350
            return { '--ck-editor-min-height': height + 'px' }
        },
    },

    mounted() {
        this._darkObserver = new MutationObserver(() => {
            this.isDark = document.documentElement.classList.contains('dark')
        })
        this._darkObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        })

        this.$nextTick(() => {
            if (!this.$refs.editorEl) return

            const base = {
                ...EDITOR_CONFIG,
                novaUploadPath:  this.field.uploadPath  || 'ck-editor',
                novaStorageDisk: this.field.storageDisk || 'public',
            }
            const config = this.field.toolbar
                ? { ...base, toolbar: { items: this.field.toolbar, shouldNotGroupWhenFull: true } }
                : base

            ClassicEditor.create(this.$refs.editorEl, config)
                .then(editor => {
                    this.editorInstance = editor
                    if (this.value) {
                        const el = document.createElement('textarea')
                        el.innerHTML = this.value
                        editor.setData(el.value)
                    }
                    editor.model.document.on('change:data', () => {
                        this.value = editor.getData()
                    })
                })
                .catch(err => console.error('[CkEditor] init failed:', err))
        })
    },

    beforeUnmount() {
        this._darkObserver?.disconnect()
        if (this.editorInstance) {
            this.editorInstance.destroy()
            this.editorInstance = null
        }
    },

    methods: {
        setInitialValue() {
            this.value = this.field.value || ''
        },

        fill(formData) {
            formData.append(this.field.attribute, this.value || '')
        },
    },
}
</script>

<style>
.ck-editor-wrapper .ck-editor__editable {
    min-height: var(--ck-editor-min-height, 350px);
    line-height: 1.75;
}

/* Dark mode */
.ck-editor--dark .ck.ck-editor__main > .ck-editor__editable,
.ck-editor--dark .ck.ck-sticky-panel .ck-toolbar {
    background-color: #0f172a !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
}
.ck-editor--dark .ck.ck-toolbar {
    background-color: #1e293b !important;
    border-color: #334155 !important;
}
.ck-editor--dark .ck.ck-toolbar .ck-button,
.ck-editor--dark .ck.ck-toolbar .ck-dropdown__button {
    color: #cbd5e1 !important;
}
.ck-editor--dark .ck.ck-toolbar .ck-button:hover:not(.ck-disabled) {
    background-color: #334155 !important;
}
.ck-editor--dark .ck.ck-toolbar .ck-button.ck-on {
    background-color: #1d4ed8 !important;
    color: #fff !important;
}
.ck-editor--dark .ck.ck-list,
.ck-editor--dark .ck.ck-dropdown__panel {
    background-color: #1e293b !important;
    border-color: #334155 !important;
}
.ck-editor--dark .ck.ck-list__item .ck-button:hover {
    background-color: #334155 !important;
}
.ck-editor--dark .ck-input {
    background-color: #0f172a !important;
    color: #cbd5e1 !important;
    border-color: #334155 !important;
}
</style>
